import awsconfig from '@/amplifyconfiguration.json';
import { ExaminationMenuDialog } from '@/features/reservation/components/ExaminationMenuDialog';
import { useExaminationMenu } from '@/features/reservation/hooks/useOutPatient';
import { ExaminationMenuType } from '@/features/reservation/types/ExaminationMenuType';
import { getPatient } from '@/graphql/queries';
import { useFacilityPatients } from '@/hooks/useFacilityPatients';
import { Backdrop, Box, CircularProgress, Grid, Typography } from '@mui/material';
import { Amplify } from 'aws-amplify';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { ReservationUrls } from '../constants/urls';
import { RESERVATION_STEP, usePageViewEvent, dialogOpenEvent } from '../hooks/useGTMEvent';
import { GetPatientQuery } from '@/API';
import { executeQuery } from '@/client/graphql';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executePost } from '@/client/rest';

Amplify.configure(awsconfig);

enum ExaminationMenuMode {
  Dynamic = 'DYNAMIC',
  Static = 'STATIC'
}

export function ExaminationMenuForm(props: any) {
  const navigate = useNavigate();
  const [examinationMenuMode, setExaminationMenuMode] = useState<ExaminationMenuMode>(ExaminationMenuMode.Static);
  const [selectedExaminationMenu, setSelectedExaminationMenu] = useState<ExaminationMenuType | null>(null);
  const [examinationMenuDialogOpen, setExaminationMenuDialogOpen] = useState<boolean>(false);
  const [membership, setMembership] = useState<any>({});
  const [vipMode, setVipMode] = useState(false);
  const [facilityInfo, setFacilityInfo] = useState<any>(null);
  const [frontMenus, setFrontMenus] = useState<any>(null);
  const facilityId = props.currentState.facilityId;
  const { getFacilityPatientsModel } = useFacilityPatients();

  const { backdropOpen, examinationMenuList, exoMenu, vipMenu } = useExaminationMenu(facilityId);
  const locate = useLocation().state;
  const outPatientId = locate?.outpatientid ? locate.outpatientid : null;
  const _props = props;
  const targetMenuName = useCallback(() => {
    if (backdropOpen) return;
    if (outPatientId === null) return;

    const targetMenuObj = examinationMenuList.filter((value) => value.outpatientId === outPatientId);
    const target = targetMenuObj.length > 0;
    if (!target) {
      navigate(`${ReservationUrls.OutpatientFallback}?fid=${facilityId}`, { replace: true });
      return;
    }
    const targetMenu = targetMenuObj[0];
    _props.setCurrentState((prevState: any) => ({
      ...prevState,
      examinationMenuId: targetMenu.outpatientId,
      examinationMenuName: targetMenu.outpatientName
    }));
    const propStep = locate?.step ?? null;
    _props.setActiveStep(propStep ?? 1);
  }, [_props, backdropOpen, outPatientId, examinationMenuList, locate, navigate]);

  useEffect(() => {
    const checkMemberStatus = async () => {
      const atr = await fetchUserAttributes();

      let vipFlag = 0;
      let memberStatus = {};
      try {
        const result = await executeQuery<GetPatientQuery>(getPatient, { id: atr.sub as string }, 'userPool');
        const patient = result.getPatient;

        const facilityPatient = await getFacilityPatientsModel(patient?.id ?? '', facilityId);

        if (facilityPatient) {
          // FacilityPatientsテーブルのレコードが存在する場合
          vipFlag = facilityPatient?.vip ? 1 : 0;
          if (facilityPatient?.memberStatus != null) {
            memberStatus = facilityPatient.memberStatus;
          }
        } else {
          // FacilityPatientテーブルのレコードが存在しない場合
          const mt = sessionStorage.getItem('mt');
          vipFlag = mt === '1' ? 1 : 0;
        }
      } catch (e) {
        if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
          Sentry.captureException(e);
        }
      }
      if (!facilityId) {
        // 施設IDがない場合は直接施設一覧画面にリダイレクト
        const redirectUrl = ReservationUrls.Reservation;
        const encodedRedirectUrl = encodeURIComponent(redirectUrl);
        navigate(`/patient/facilities?redirect=${encodedRedirectUrl}`, { replace: true });
        return;
      }

      const facility_json = (await executePost({
        apiName: 'medirecoWebApi',
        path: '/get-facility-info',
        options: {
          queryParams: { facilityId: facilityId }
        }
      })) as any;

      setMembership({ vip: vipFlag === 1 ? true : false, ...memberStatus! });
      setFacilityInfo((facility_json as any)['info']);
      setFrontMenus((facility_json as any)['menus']);
    };
    checkMemberStatus();
    targetMenuName();
  }, []);

  usePageViewEvent({ pageTitle: RESERVATION_STEP.Menu, facilityId: props.currentState.facilityId });

  const onSelectMenu = (examinationMenuId: string) => {
    const menu = examinationMenuList.find((menu) => menu.outpatientId === examinationMenuId);
    if (menu) {
      setSelectedExaminationMenu(menu);
      setExaminationMenuDialogOpen(true);
      dialogOpenEvent({ dialogTitle: RESERVATION_STEP.MenuConfirm, facilityId: props.currentState.facilityId });
    } else {
      console.error('Menu not found');
      navigate(`${ReservationUrls.OutpatientFallback}?fid=${facilityId}`, { replace: true });
      return;
    }
  };

  const onSubmit = () => {
    if (selectedExaminationMenu) {
      props.setCurrentState({
        ...props.currentState,
        examinationMenuId: selectedExaminationMenu.outpatientId,
        examinationMenuName: selectedExaminationMenu.outpatientName
      });
      props.handleNext();
    } else {
      console.error('Menu not selected');
      navigate(`${ReservationUrls.OutpatientFallback}?fid=${facilityId}`, { replace: true });
      return;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={0} md={3} sm={2} />
        <Grid item xs={12} md={6} sm={8}>
          {facilityInfo !== null && (
            <Grid container direction={'column'} spacing={2}>
              <Grid item xs={12} sx={{ textAlign: 'center', maxWidth: '100% !important', overflowX: 'hidden' }}>
                <Typography mt={1}>ご覧のメニューをご予約いただけます。</Typography>
                <Typography mt={1} className="font-bold">
                  いずれの予約についても
                  <span className="text-primary">予約時間の{facilityInfo?.limit_minutes}分前</span>
                  までにご来院ください。
                </Typography>
              </Grid>

              <Grid item mt={2} xs={12} sx={{ maxWidth: '100% !important', overflowX: 'hidden', textAlign: 'center' }}>
                <Typography className="text-primary font-bold	">診療時間</Typography>
                <div className="w-fit m-auto text-left">
                  {JSON.parse(facilityInfo.working_info || {}).map((info: any) => {
                    return (
                      <Typography key={uuidv4()}>
                        {`${info.day} ${info.hours
                          .map((time: string[]) => {
                            return `${time[0]}～${time[1]}`;
                          })
                          .join(' / ')}`}
                        <br />
                      </Typography>
                    );
                  })}
                </div>

                <Typography mt={1} sx={{ textAlign: 'center' }}>
                  <span className="text-primary font-bold	">休診日</span>
                  <br />
                  {JSON.parse(facilityInfo.closed_days || {}).join(' / ')}
                </Typography>
              </Grid>

              <Grid item xs={12} mt={2} sx={{ maxWidth: '100% !important' }}>
                <Grid container spacing={2} sx={{ textAlign: 'center', justifyContent: 'center' }}>
                  {examinationMenuMode === ExaminationMenuMode.Dynamic ? (
                    <Grid item key={uuidv4()}>
                      <ChangeMenuButton
                        label={frontMenus?.find((b: any) => b.menu_switch_flag && b.is_dynamic)?.label}
                        size={'text-xs'}
                        onClick={() => {
                          setVipMode(false);
                          setExaminationMenuMode(ExaminationMenuMode.Static);
                        }}
                      />
                    </Grid>
                  ) : null}
                  {examinationMenuMode === ExaminationMenuMode.Dynamic ? (
                    (vipMode ? vipMenu : examinationMenuList).map((menu) => (
                      <Grid item key={uuidv4()}>
                        <ConsultationMenuButton
                          label={menu.outpatientName}
                          onClick={() => {
                            onSelectMenu(menu.outpatientId);
                          }}
                        />
                      </Grid>
                    ))
                  ) : (
                    <>
                      {membership.vip && (
                        <Grid item key={uuidv4()}>
                          <Typography mt={1} className="font-bold" textAlign={'left'}>
                            VIP会員の方はこちら
                          </Typography>
                          <ConsultationMenuButton
                            label={'VIP'}
                            onClick={() => {
                              setVipMode(true);
                              setExaminationMenuMode(ExaminationMenuMode.Dynamic);
                            }}
                          />
                        </Grid>
                      )}
                      {membership.exo && (
                        <Grid item key={uuidv4()}>
                          <Typography mt={1} className="font-bold" textAlign={'left'}>
                            エクソソーム会員の方はこちら
                          </Typography>
                          <ConsultationMenuButton
                            label={'エクソソーム'}
                            onClick={() => {
                              setSelectedExaminationMenu(exoMenu[0]);
                              setExaminationMenuDialogOpen(true);
                              dialogOpenEvent({
                                dialogTitle: RESERVATION_STEP.MenuConfirm,
                                facilityId: props.currentState.facilityId
                              });
                            }}
                          />
                        </Grid>
                      )}
                      {frontMenus !== null &&
                        frontMenus
                          .filter((button: any) => !button.is_dynamic)
                          .map((button: any) => {
                            return (
                              <Grid item key={uuidv4()}>
                                {button.title !== null && (
                                  <Typography mt={1} className="font-bold" textAlign={'left'}>
                                    {button.title}
                                  </Typography>
                                )}
                                {button.description !== null && (
                                  <Typography
                                    mt={1}
                                    fontSize={'10pt'}
                                    textAlign={'left'}
                                    marginTop={'0px'}
                                    color={JSON.parse(button.description).color || 'inherit'}
                                  >
                                    {JSON.parse(button.description).text || ''}
                                  </Typography>
                                )}
                                {button.menu_switch_flag ? (
                                  <ChangeMenuButton
                                    label={button.label}
                                    onClick={() => {
                                      setExaminationMenuMode(ExaminationMenuMode.Dynamic);
                                    }}
                                  />
                                ) : (
                                  <ConsultationMenuButton
                                    label={button.label}
                                    onClick={() => {
                                      onSelectMenu(button.outpatient_id!);
                                    }}
                                  />
                                )}
                              </Grid>
                            );
                          })}
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid
                item
                mt={2}
                xs={12}
                sx={{ textAlign: 'left', maxWidth: '100% !important', '& :last-child': { marginBottom: 0 } }}
              >
                <Typography mb={3} sx={{ textAlign: 'center' }}>
                  <span className="text-primary font-bold">ご注意事項</span>
                </Typography>
                {JSON.parse(facilityInfo.attentions || {}).map((v: string) => {
                  return (
                    <Typography key={uuidv4()} mt={1} mb={3}>
                      ・{v}
                    </Typography>
                  );
                })}
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={0} md={3} sm={2} />
      </Grid>
      <ExaminationMenuDialog
        open={examinationMenuDialogOpen}
        menu={selectedExaminationMenu}
        submitHandler={onSubmit}
        closeHandler={() => setExaminationMenuDialogOpen(false)}
      />
      <Backdrop
        sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={backdropOpen}
      >
        <CircularProgress color={'inherit'} />
      </Backdrop>
    </Box>
  );
}

interface MenuButtonProps {
  label: string;
  size?: 'text-xs' | 'text-sm' | 'text-base' | 'text-lg' | 'text-xl';
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}
const ConsultationMenuButton = ({ label, onClick }: MenuButtonProps) => {
  return (
    <button
      onClick={onClick}
      style={{ width: '353px', height: '48px', color: '#FFFFFF', textAlign: 'left', margin: 'auto' }}
      className="bg-primary flex relative rounded"
    >
      <div className="text-base m-auto font-bold" style={{ whiteSpace: 'pre-wrap' }}>
        {label}
      </div>
      {/* <div>
        <ArrowForwardIos style={{ position: 'absolute', top: '16px', right: '11px', fontSize: '16px' }} />
      </div> */}
    </button>
  );
};
const ChangeMenuButton = ({ label, size = 'text-base', onClick }: MenuButtonProps) => {
  return (
    <button
      onClick={onClick}
      style={{
        width: '353px',
        height: '48px',
        color: '#FFFFFF',
        textAlign: 'left',
        margin: 'auto',
        backgroundColor: '#666666'
      }}
      className="flex relative rounded"
    >
      <div className={size + ' m-auto font-bold'} style={{ whiteSpace: 'pre-wrap' }}>
        {label}
      </div>
      {/* <div>
        <ArrowForwardIos style={{ position: 'absolute', top: '16px', right: '11px', fontSize: '16px' }} />
      </div> */}
    </button>
  );
};
